import { useState } from 'react';
import { AllOptional } from './Types';

export enum ApplicationStorageKeys {
    MedicationsBrowserFilters = 'Medications.MedicationsBrowserFilters',
    MedicationsSummaryFilters = 'Medications.MedicationsSummaryFilters',
    PharmacyRequestsListFilters = 'Medications.PharmacyRequestsListFilters',
    MedicationSettings = 'Medications.MedicationSettings',
    StateTerritoryTermSettings = 'Medications.StateTerritoryTermSettings',
    MedicationListFilters = 'Medications.MedicationList.Filter',
    MedicationsSummaryTableSorting = 'Medications.MedicationsSummaryTableSorting',
    MedicationsSummaryS8MultiReprescribe = 'Medications.SummaryS8MultiReprescribe'
}

export function deleteFromLocalStorage(key: ApplicationStorageKeys, keySuffix?: string) {
    const targetKey = keySuffix ? `${key}-${keySuffix}` : key;
    localStorage.removeItem(targetKey);
}

export function useLocalStorage<TConfig extends { [key: string]: any }>(
    key: ApplicationStorageKeys,
    defaults: TConfig,
    keySuffix?: string
) {
    const [state, setState] = useState(loadFromLocalStorage(key, defaults, keySuffix));

    const updateValue = (newValue: TConfig) => {
        setState(newValue);
        saveToLocalStorage(key, newValue, keySuffix);
    };

    return {
        value: state,
        updateValue
    };
}

export function loadFromLocalStorage<TConfig extends { [key: string]: any }>(
    key: ApplicationStorageKeys,
    defaults: TConfig,
    keySuffix?: string
): TConfig {
    const targetKey = keySuffix ? `${key}-${keySuffix}` : key;

    const dehydratedPreferences: string | null = localStorage.getItem(targetKey);

    let storedPreferences: AllOptional<TConfig>;
    let restoredPreferences: TConfig;

    if (dehydratedPreferences) {
        try {
            storedPreferences = JSON.parse(dehydratedPreferences);
            restoredPreferences = {
                ...defaults
            };
        } catch (err) {
            console.error('Unable to parse saved settings, returning defaults');
            return defaults;
        }

        Object.entries(defaults).forEach(([defaultKey, defaultValue]: [keyof TConfig, any]) => {
            if (typeof storedPreferences[defaultKey] === 'undefined') {
                restoredPreferences[defaultKey] = defaultValue;
            } else {
                restoredPreferences[defaultKey] = storedPreferences[defaultKey] as any;
            }
        });

        return {
            ...storedPreferences,
            ...restoredPreferences
        };
    } else return defaults;
}

export function saveToLocalStorage<TConfig extends { [key: string]: any }>(
    key: ApplicationStorageKeys,
    object: TConfig,
    keySuffix?: string
) {
    const targetKey = keySuffix ? `${key}-${keySuffix}` : key;
    const stringyFilters = JSON.stringify(object);

    localStorage.setItem(targetKey, stringyFilters);
}
